import React, { forwardRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaFilePdf, FaFileWord, FaFileAlt, FaTimes, FaDownload, FaTag } from 'react-icons/fa';
import { downloadFileAs } from '../../utils/downloadFile';
import styles from './ProfilePage.module.css';

const WORD_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const getKind = (docItem) => {
  const ext = (docItem.fileName || '').split('.').pop().toLowerCase();
  if (docItem.fileType === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (WORD_TYPES.includes(docItem.fileType) || ext === 'doc' || ext === 'docx') return 'word';
  return 'other';
};

const KIND_ICON = { pdf: <FaFilePdf />, word: <FaFileWord />, other: <FaFileAlt /> };

/**
 * Read-only list of documents assigned to the user (Policies, Job Descriptions).
 * Clicking an item opens an in-page viewer; each item and the viewer offer a download.
 */
const AssignedDocumentList = forwardRef(({
  id, title, documents, loading, error, searchPlaceholder, emptyText
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);

  const term = searchTerm.toLocaleLowerCase('tr');
  const filtered = documents.filter(d =>
    d.name.toLocaleLowerCase('tr').includes(term) ||
    (d.subtitle || '').toLocaleLowerCase('tr').includes(term)
  );

  const handleDownload = (event, docItem) => {
    event.stopPropagation();
    downloadFileAs(docItem.fileUrl, docItem.fileName || docItem.name);
  };

  const selectedKind = selected ? getKind(selected) : null;

  return (
    <motion.div
      className={styles.fileListContainer}
      id={id}
      ref={ref}
      tabIndex={-1}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
    >
      <h3 className={styles.listTitle}>{title}</h3>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        {searchTerm && (
          <button className={styles.clearSearchBtn} onClick={() => setSearchTerm('')} title="Aramayı temizle">
            <FaTimes />
          </button>
        )}
      </div>

      <div className={styles.fileListWrapper}>
        {loading ? (
          <p className={styles.emptyMessage}>Yükleniyor...</p>
        ) : error ? (
          <p className={styles.emptyMessage}>{error}</p>
        ) : filtered.length > 0 ? (
          <ul className={styles.documentList}>
            {filtered.map(docItem => (
              <motion.li
                key={docItem.id}
                className={styles.documentItem}
                onClick={() => setSelected(docItem)}
                whileHover={{ x: 5 }}
              >
                <div className={styles.documentTypeIcon}>{KIND_ICON[getKind(docItem)]}</div>
                <div className={styles.documentInfo}>
                  <span className={styles.documentName}>{docItem.name}</span>
                  {docItem.version && <span className={styles.documentVersion}>v{docItem.version}</span>}
                  {docItem.subtitle && <span className={styles.documentVersion}>{docItem.subtitle}</span>}
                </div>
                {docItem.fileUrl && (
                  <button
                    type="button"
                    className={styles.documentDownloadBtn}
                    onClick={(e) => handleDownload(e, docItem)}
                    title="İndir"
                    aria-label={`${docItem.name} indir`}
                  >
                    <FaDownload />
                  </button>
                )}
              </motion.li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyMessage}>
            {searchTerm ? `"${searchTerm}" ile eşleşen doküman bulunamadı.` : emptyText}
          </p>
        )}
      </div>

      <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered className={styles.modal}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            {selectedKind && KIND_ICON[selectedKind]} {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {selected && (
            <div className={styles.sopModalContent}>
              <div className={styles.sopDocumentInfo}>
                <div className={styles.sopDocumentIcon}>{KIND_ICON[selectedKind]}</div>
                <div className={styles.sopDocumentDetails}>
                  <h4 className={styles.sopDocumentTitle}>{selected.name}</h4>
                  <div className={styles.sopDocumentMeta}>
                    {selected.version && <span><FaTag /> Versiyon {selected.version}</span>}
                    {selected.subtitle && <span>{selected.subtitle}</span>}
                  </div>
                  {selected.description && <p className={styles.fileCardDesc}>{selected.description}</p>}
                </div>
              </div>

              {!selected.fileUrl ? (
                <div className={styles.unsupportedFile}>
                  <p>Bu doküman için dosya bulunmamaktadır.</p>
                </div>
              ) : selectedKind === 'other' ? (
                <div className={styles.unsupportedFile}>
                  <FaFileAlt style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: '1rem' }} />
                  <p>Bu dosya türü görüntülenemiyor. Lütfen indirerek açınız.</p>
                </div>
              ) : (
                <div className={styles.sopViewerContainer}>
                  <div className={styles.pdfContainer}>
                    <iframe
                      src={selectedKind === 'pdf'
                        ? `${selected.fileUrl}#navpanes=0`
                        : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selected.fileUrl)}`}
                      title={selected.name}
                      width="100%"
                      height="550px"
                      className={styles.pdfFrame}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>Kapat</Button>
          {selected?.fileUrl && (
            <Button variant="primary" onClick={(e) => handleDownload(e, selected)}>
              <FaDownload /> İndir
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
});

AssignedDocumentList.displayName = 'AssignedDocumentList';

export default AssignedDocumentList;
